/**
 * Multi-client types.
 * ClientId from VITE_CLIENT_ID; ClientProfile holds theme + enabledModules.
 * Each client can have a different style, branding, and design system.
 *
 * Note: Some clients have specific features; others do not. Feature availability
 * is per-client: ClientProfile.enabledModules defines which modules (routes/nav)
 * are available for that client. Identity has full set; GO/Salam have subsets.
 */

export type ClientId = 'identity' | 'go' | 'salam';

/** Valid client IDs (single source for guards and env parsing). */
export const VALID_CLIENT_IDS: readonly ClientId[] = ['identity', 'go', 'salam'];

export function isClientId (value: string | undefined): value is ClientId {
  return VALID_CLIENT_IDS.includes(value as ClientId);
}

/** Optional design-system overrides per client (colors, typography, etc.) */
export interface ClientDesignSystem {
  /** Primary palette (maps to Tailwind/semantic usage) */
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    error?: string;
    warning?: string;
    success?: string;
    grayText?: string;
    grayUnit?: string;
  };
  /** Typography: font family for body/headings (e.g. Zain, Tajawal, or client font) */
  typography?: {
    fontFamily?: string;
    fontFamilyHeading?: string;
  };
  /** Optional: CSS class name or data attribute to scope client-specific styles (e.g. "theme-go", "theme-salam") */
  themeClass?: string;
  /** Optional: border radius token (e.g. "rounded-lg" vs "rounded-xl") */
  borderRadius?: string;
}

export interface Theme {
  appTitle: string;
  brandName: string;
  logoUrl?: string;
  /** Light/white variant of the logo, for use on dark/colored backgrounds (e.g. login screen) */
  loginLogoUrl?: string;
  /** Background image for the login screen right panel */
  loginBackgroundUrl?: string;
  primaryColor?: string;
  /**
   * Per-client design system: each client (identity / go / salam) can have different
   * colors, typography, and styling. Use this for client-specific design tokens;
   * UI can read from useClientTheme().designSystem and apply via inline styles or
   * a scoped theme class.
   */
  designSystem?: ClientDesignSystem;
}

export type ModuleId =
  | 'dashboard'
  | 'map'
  | 'stores-map'
  | 'products'
  | 'inventory'
  | 'distribution'
  | 'marketers'
  | 'marketer-tracking'
  | 'service-providers'
  | 'stores'
  | 'store-deliveries'
  | 'visits'
  | 'payments'
  | 'sales'
  | 'users'
  | 'reports'
  | 'reports-marketer'
  | 'reports-products-sales'
  | 'provider'
  | 'add-stock'
  | 'checklists';

/** Fine-grained feature flags within a module (not module-level gating). */
export interface ClientFeatures {
  /** Whether the serial numbers feature is enabled. Identity only; GO/Salam return 403 FEATURE_DISABLED. */
  serialsEnabled: boolean;
  /** Whether inventory management (stock/quantity changes) is enabled. Identity only. */
  inventoryEnabled: boolean;
}

/**
 * Client profile: identity, theme, and which features (modules) are enabled.
 * Some clients have specific features; others don't — enabledModules is the single source.
 */
export interface ClientProfile {
  clientId: ClientId;
  theme: Theme;
  /** Modules this client has. Routes and nav are built only from these; other clients may have different sets. */
  enabledModules: ModuleId[];
  defaultLandingPath: string;
  /** Fine-grained feature flags within modules. Defaults to all-disabled when omitted. */
  features?: ClientFeatures;
}
